// Componente Button reutilizable con variantes y estados
import { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'

// Estilos por variante usando inline styles
const variantStyles = {
  primary: {
    background: '#00C2FF',
    color: '#061530',
    fontWeight: 600,
    boxShadow: '0 0 20px rgba(0, 194, 255, 0.15)',
  },
  secondary: {
    background: 'var(--color-btn-secondary-bg)',
    color: 'var(--color-btn-secondary-text)',
    border: '1px solid var(--color-btn-secondary-border)',
    backdropFilter: 'blur(16px)',
  },
  outline: {
    background: 'transparent',
    color: '#00C2FF',
    border: '2px solid #00C2FF',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--color-btn-ghost-text)',
  },
  danger: {
    background: '#EF4444',
    color: 'white',
    fontWeight: 600,
  },
  success: {
    background: '#22C55E',
    color: 'white',
    fontWeight: 600,
  },
}

const sizeStyles = {
  sm: { padding: '6px 12px', fontSize: '0.8125rem', borderRadius: '6px', gap: '6px' },
  md: { padding: '10px 20px', fontSize: '0.875rem', borderRadius: '8px', gap: '8px' },
  lg: { padding: '12px 24px', fontSize: '1rem', borderRadius: '12px', gap: '10px' },
  xl: { padding: '16px 32px', fontSize: '1.125rem', borderRadius: '12px', gap: '12px' },
}

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon: Icon,
  iconRight: IconRight,
  className = '',
  onClick,
  type = 'button',
  style: customStyle = {},
  ...props
}, ref) => {
  const isDisabled = disabled || loading

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Outfit', sans-serif",
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    border: 'none',
    whiteSpace: 'nowrap',
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...customStyle,
  }

  return (
    <motion.button
      ref={ref}
      type={type}
      whileHover={!isDisabled ? { scale: 1.02 } : {}}
      whileTap={!isDisabled ? { scale: 0.98 } : {}}
      onClick={onClick}
      disabled={isDisabled}
      className={className}
      style={baseStyle}
      {...props}
    >
      {loading ? (
        <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
      ) : Icon ? (
        <Icon style={{ width: '16px', height: '16px' }} />
      ) : null}
      {children}
      {IconRight && !loading && <IconRight style={{ width: '16px', height: '16px' }} />}
    </motion.button>
  )
})

Button.displayName = 'Button'

export default Button
